/**
 * Entity Code Auto-Generator for APV Packaging ERP
 * Automatically generates concise, human-readable codes (<= 14 characters)
 * for Material Types and Product Types based on industry naming conventions.
 */

/**
 * Remove Vietnamese diacritics and convert to ASCII
 */
export function removeVietnameseTones(str: string): string {
  if (!str) return "";
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .replace(/[^a-zA-Z0-9\s-_]/g, " ")
    .trim();
}

/**
 * Sanitize code to valid uppercase alphanumeric with dashes/underscores, max length 14
 */
export function sanitizeCode(code: string, maxLength = 14): string {
  if (!code) return "";
  return code
    .toUpperCase()
    .replace(/[^A-Z0-9-_]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, maxLength);
}

// ── MATERIAL TYPE CODE GENERATOR ───────────────────────────────────────────────
const MATERIAL_SHORT_MAP: Record<string, string> = {
  IVORY: "IVO",
  DUPLEX: "D",
  COUCHE: "C",
  BRISTOL: "BRI",
  FORD: "FOD",
  KRAFT: "KFT",
  DECAL: "DCL",
  "DECAL GIAY": "DCL-G",
  "DECAL NHUA": "DCL-N",
  "DECAL VO": "DCL-V",
  METALIZE: "MET",
  CARBONLESS: "CBN",
  "SONG E": "SE",
  "SONG B": "SB",
  "SONG C": "SC",
  "SONG AB": "SAB",
  "SONG 2 LOP": "SE-2L",
  "SONG 3 LOP": "S3L",
  "SONG 5 LOP": "S5L",
  "SONG 7 LOP": "S7L",
  MUC: "MUC",
  "MUC IN": "MUC",
  MANG: "MANG",
  "MANG BOPP": "MANG-BOPP",
  "MANG BONG": "MANG-BONG",
  "MANG MO": "MANG-MO",
  "KEO DAN": "KEO",
  KEO: "KEO",
  "KEM IN": "KEM",
  "BAN KEM": "KEM",
};

export interface MaterialTypeCodeParams {
  material?: string;
  gsm?: number | string;
  group?: string;
  name?: string;
}

export function generateMaterialTypeCode(params: MaterialTypeCodeParams): string {
  const { material = "", gsm, group = "", name = "" } = params;

  const cleanMaterial = removeVietnameseTones(material).toUpperCase().trim();
  const cleanName = removeVietnameseTones(name).toUpperCase().trim();
  const cleanGroup = removeVietnameseTones(group).toUpperCase().trim();

  // 1. Determine material prefix
  let prefix = "";
  for (const [key, short] of Object.entries(MATERIAL_SHORT_MAP)) {
    if (cleanMaterial === key || cleanMaterial.includes(key)) {
      prefix = short;
      break;
    }
  }

  if (!prefix) {
    // Try from name
    for (const [key, short] of Object.entries(MATERIAL_SHORT_MAP)) {
      if (cleanName.includes(key)) {
        prefix = short;
        break;
      }
    }
  }

  if (!prefix) {
    // Fallback: take first word or abbreviation of clean material / group
    const words = (cleanMaterial || cleanGroup || cleanName).split(/\s+/).filter(Boolean);
    if (words.length === 1) {
      prefix = words[0].slice(0, 4);
    } else if (words.length > 1) {
      prefix = words.map((w) => w[0]).join("").slice(0, 4);
    }
  }

  // 2. Determine GSM suffix
  const gsmValue = gsm ? String(gsm).trim() : "";
  let code = "";

  if (prefix && gsmValue) {
    // E.g. IVO-300 or D350 or C150
    if (prefix.length === 1) {
      code = `${prefix}${gsmValue}`;
    } else {
      code = `${prefix}-${gsmValue}`;
    }
  } else if (prefix) {
    code = prefix;
  } else if (gsmValue) {
    code = `GSM-${gsmValue}`;
  } else if (cleanName) {
    const parts = cleanName.split(/\s+/);
    code = parts.slice(0, 2).join("-");
  }

  return sanitizeCode(code || "VT", 14);
}

// ── PRODUCT TYPE CODE GENERATOR ───────────────────────────────────────────────
const PRODUCT_CATEGORY_SHORT_MAP: Record<string, string> = {
  "HOP CARTON BOI": "HOP-CAR",
  "HOP CARTON": "CARTON",
  "HOP CUNG": "HOP-CUNG",
  "HOP GIAY": "HOP-GIAY",
  "HOP MEM": "HOP-MEM",
  "TUI GIAY": "TUI-GIAY",
  "TEM NHAN": "TEM-NHAN",
  "DECAL CUON": "DCL-CUON",
  "DECAL TO": "DCL-TO",
  "THIET KE": "TKE",
  "THE TREO": "THE-TREO",
  "TAG MAC": "TAG-MAC",
  "TO GAP": "TO-GAP",
  BROCHURE: "BROCHURE",
  CATALOGUE: "CATALOG",
  "SO TAY": "SO-TAY",
  LICH: "LICH",
  "BAO BI DUOC": "BB-DUOC",
  "BAO BI MY PHAM": "BB-MYPHAM",
  "BAO BI THUC PHAM": "BB-TPHAM",
};

export interface ProductTypeCodeParams {
  category?: string;
  group?: string;
  name?: string;
}

export function generateProductTypeCode(params: ProductTypeCodeParams): string {
  const { category = "", group = "", name = "" } = params;

  const cleanCategory = removeVietnameseTones(category).toUpperCase().trim();
  const cleanGroup = removeVietnameseTones(group).toUpperCase().trim();
  const cleanName = removeVietnameseTones(name).toUpperCase().trim();

  // 1. Try category map match
  let matched = "";
  for (const [key, short] of Object.entries(PRODUCT_CATEGORY_SHORT_MAP)) {
    if (cleanCategory === key || cleanCategory.includes(key)) {
      matched = short;
      break;
    }
  }

  if (!matched && cleanName) {
    for (const [key, short] of Object.entries(PRODUCT_CATEGORY_SHORT_MAP)) {
      if (cleanName.includes(key)) {
        matched = short;
        break;
      }
    }
  }

  if (!matched && cleanGroup) {
    for (const [key, short] of Object.entries(PRODUCT_CATEGORY_SHORT_MAP)) {
      if (cleanGroup.includes(key)) {
        matched = short;
        break;
      }
    }
  }

  if (!matched) {
    const text = cleanCategory || cleanGroup || cleanName;
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      matched = `${words[0].slice(0, 3)}-${words[1].slice(0, 3)}`;
    } else if (words.length === 1) {
      matched = words[0].slice(0, 8);
    }
  }

  return sanitizeCode(matched || "SP", 14);
}
